import { ref, watch } from 'vue'

import type { Ref } from 'vue'

import { useDisciplineStore } from '@entities/customer'
import { AppRoute } from '@shared/config'
import { resolveUrl } from '@shared/lib/browser'

// LiveKit surfaces the DisconnectReason enum KEY as a string; a server removeParticipant
// disconnect arrives as 'PARTICIPANT_REMOVED' (see room-live-kit.hook -> handleDisconnected).
// Comparing the string keeps this decision free of any livekit-client import.
const REMOVED_REASON = 'PARTICIPANT_REMOVED'

interface RemovedRouteContext {
  kid: string
  isMath: boolean
}

export const isRemovedReason = (reason: string | null): boolean => reason === REMOVED_REASON

// The removed kid goes to their discipline home — the same non-paid target the terminal flow uses.
export const getRemovedRoute = (context: RemovedRouteContext): string | null =>
  resolveUrl(context.isMath ? AppRoute.HomeMath : AppRoute.HomeEla, { kid: context.kid })

interface LessonRoomRemovedReturn {
  wasRemoved: Ref<boolean>
}

export const useLessonRoomRemoved = (lastDisconnectReason: Ref<string | null>): LessonRoomRemovedReturn => {
  const disciplineStore = useDisciplineStore()
  const route = useRoute()

  const wasRemoved = ref<boolean>(false)

  // A tutor kicked the kid: leave the room to home and never offer Reconnect (the token is
  // still valid, so a rejoin would defeat the removal). Latch once — ignore later disconnects.
  watch(lastDisconnectReason, (reason) => {
    if (wasRemoved.value || !isRemovedReason(reason)) return

    wasRemoved.value = true

    const url = getRemovedRoute({
      kid: String(route.params.kid ?? ''),
      isMath: disciplineStore.isMath,
    })

    if (url) void navigateTo(url)
  })

  return { wasRemoved }
}
